import { useTranslation } from "react-i18next";
import { Checkbox, Form, Grid, GridColumn, Input, Label } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { QUESTION_OPTIONS } from "models";
import { useTagsQuery } from "modules/QuestionsDashboard/hooks";

import type { Question } from "models";

type EditQuestionFormProps = {
  canEdit: boolean;
};

const FormHelper = createFormHelper<Question>();

export const EditQuestionForm = ({ canEdit }: EditQuestionFormProps) => {
  const { t } = useTranslation();

  const { setValue, getValues } = FormHelper.useFormContext();
  const title = FormHelper.useWatch("title");
  const evhcLaborPercent = FormHelper.useWatch("evhc_labor_percent");
  const evhcPartPercent = FormHelper.useWatch("evhc_part_percent");
  const evhcTirePercent = FormHelper.useWatch("evhc_tire_percent");
  const evhcExternalPercent = FormHelper.useWatch("evhc_external_percent");
  const questionOptions = FormHelper.useWatch("question_options") ?? [];
  const checklists = getValues("checklist") ?? [];
  const { data: tagsOptions = [] } = useTagsQuery();

  const isEvhcExceeded = evhcLaborPercent + evhcPartPercent + evhcTirePercent + evhcExternalPercent > 100;

  const questionOptionLabels: Record<QUESTION_OPTIONS, string> = {
    [QUESTION_OPTIONS.PriceEnabled]: t("v8_price_enabled", "Price enabled"),
    [QUESTION_OPTIONS.SnoozeEnabled]: t("v8_snooze_enabled", "Snooze enabled"),
    [QUESTION_OPTIONS.PinEnabled]: t("v8_pin_enabled", "Pin enabled"),
    [QUESTION_OPTIONS.DisableOkWithoutNote]: t("v8_disable_ok_without_note", "Disable OK without note"),
    [QUESTION_OPTIONS.HideXButton]: t("v8_hide_x_button", "Hide X button"),
    [QUESTION_OPTIONS.PhotoMandatory]: t("v8_photo_mandatory", "Photo mandatory"),
    [QUESTION_OPTIONS.HideCustomerOkAndConvertToIntervention]: t("v8_hide_customer_ok_convert_to_intervention", "Hide Customer OK / Convert to intervention")
  };

  const handleToggleOption = (optionIdx: number, value: boolean) =>
    setValue("question_options", questionOptions.with(optionIdx, { ...questionOptions[optionIdx], value }), { shouldDirty: true });

  const handleEvhcPercentChange = (value: string, onChange: (evhcPercent: number) => void) => {
    if ([...value].some(v => v < "0" || v > "9")) return;

    onChange(value ? parseInt(value, 10) : 0);
  };

  return (
    <>
      <Form.Field required error={!title?.trim()}>
        <label>{t("v8_title", "Title")}</label>
        <FormHelper.Field.Input name="title" inputProps={{ placeholder: t("v8_title", "Title"), fluid: true, disabled: !canEdit }} />
      </Form.Field>

      <Grid>
        <GridColumn width={11}>
          <Form.Field>
            <label>{t("v8_tags", "Tags")}</label>
            <FormHelper.Field.Dropdown
              name="tag_ids"
              options={tagsOptions}
              dropdownProps={{ placeholder: t("v8_tags", "Tags"), fluid: true, multiple: true, search: true, disabled: !canEdit }}
            />
          </Form.Field>
        </GridColumn>

        <GridColumn width={5}>
          {!!checklists.length && (
            <Form.Field>
              <label>{t("v8_checklist_usages", "Checklist usages")}</label>
              <div className="checklist-usages">
                {checklists.map(checklist => (
                  <Label key={checklist.id} color="blue">
                    {checklist.name}
                  </Label>
                ))}
              </div>
            </Form.Field>
          )}
        </GridColumn>
      </Grid>

      <h3 className="evhc-percents-title">{t("v8_evhc_percentages", "EVHC Percentages")}</h3>
      <Form.Group widths="equal">
        <Form.Field>
          <label>{t("v8_labor_percent", "Labor %")}</label>
          <FormHelper.Controller
            name="evhc_labor_percent"
            render={({ field }) => (
              <Input fluid inputMode="numeric" disabled={!canEdit} value={field.value} onChange={(_, { value }) => handleEvhcPercentChange(value, field.onChange)} />
            )}
          />
        </Form.Field>
        <Form.Field>
          <label>{t("v8_part_percent", "Part %")}</label>
          <FormHelper.Controller
            name="evhc_part_percent"
            render={({ field }) => (
              <Input fluid inputMode="numeric" disabled={!canEdit} value={field.value} onChange={(_, { value }) => handleEvhcPercentChange(value, field.onChange)} />
            )}
          />
        </Form.Field>
        <Form.Field>
          <label>{t("v8_tire_percent", "Tire %")}</label>
          <FormHelper.Controller
            name="evhc_tire_percent"
            render={({ field }) => (
              <Input fluid inputMode="numeric" disabled={!canEdit} value={field.value} onChange={(_, { value }) => handleEvhcPercentChange(value, field.onChange)} />
            )}
          />
        </Form.Field>
        <Form.Field>
          <label>{t("v8_external_percent", "External %")}</label>
          <FormHelper.Controller
            name="evhc_external_percent"
            render={({ field }) => (
              <Input fluid inputMode="numeric" disabled={!canEdit} value={field.value} onChange={(_, { value }) => handleEvhcPercentChange(value, field.onChange)} />
            )}
          />
        </Form.Field>
      </Form.Group>

      {isEvhcExceeded && <p className="evhc-percents-error">{t("v8_evhc_percentages_exceeded", "The EVHC percent values must not exceed 100%")}</p>}

      <Form.Field>
        <label>{t("v8_question_settings", "Question settings")}</label>
        <Form.Group inline className="question-toggles">
          <FormHelper.Field.Checkbox name="active" label={t("v8_active", "Active")} disabled={!canEdit} />
          <FormHelper.Field.Checkbox name="can_duplicate" label={t("v8_can_duplicate", "Can duplicate")} disabled={!canEdit} />

          {questionOptions.map((questionOption, optionIdx) => (
            <Form.Field key={questionOption.id}>
              <Checkbox
                label={questionOptionLabels[questionOption.id]}
                checked={questionOption.value}
                disabled={!canEdit}
                onChange={(_, { checked }) => handleToggleOption(optionIdx, !!checked)}
              />
            </Form.Field>
          ))}
        </Form.Group>
      </Form.Field>
    </>
  );
};
